/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, { useEffect } from "react";
import Loadable from "react-loadable";
import { Redirect, Route, Switch } from "react-router-dom";
import Dashboard from "../../features/dashboard";
import MapPage from "../../features/map/index";

// cspell:disable-next-line
import Announcement from "../common/anouncement";

import CheckVersion from "../common/util/CheckVersion";
import { useAuth } from "../context/auth";
import DataProvider from "../context/data";
import { debugLog } from "../helpers/debug.js";
import LoadingComponent from "./LoadingComponent";
import LoadingWithTimeout from "./LoadingWithTimeout";
import SideDrawer from "./navigation/SideDrawer/SideDrawer";

const AsyncKegFill = Loadable({
  loader: () => import("../../features/kegs/kegFill"),
  loading: LoadingComponent,
});

const AsyncWarehouseTransfer = Loadable({
  loader: () => import("../../features/stockControl/warehouseTransfer"),
  loading: LoadingComponent,
});

const AsyncKegList = Loadable({
  loader: () => import("../../features/kegs/kegList"),
  loading: LoadingComponent,
});

const AsyncKegDetailedPage = Loadable({
  loader: () => import("../../features/kegs/KegDetailedPage/KegDetailedPage"),
  loading: LoadingComponent,
});

const AsyncKegBatchUpdatePage = Loadable({
  loader: () => import("../../features/kegs/KegBatchUpdate/KegBatchUpdate"),
  loading: LoadingComponent,
});

const AsyncKegQuickViewPage = Loadable({
  loader: () => import("../../features/kegs/KegQuickView/KegQuickView"),
  loading: LoadingComponent,
});

const AsyncInspectionPilePage = Loadable({
  loader: () => import("../../features/kegs/InspectionPile"),
  loading: LoadingComponent,
});

const AsyncOnboardKegsPage = Loadable({
  loader: () => import("../../features/kegs/OnboardKegs"),
  loading: LoadingComponent,
});

const AsyncFixKegPage = Loadable({
  loader: () => import("../../features/kegs/InspectionPile/Fix"),
  loading: LoadingComponent,
});

const AsyncIssueKegPage = Loadable({
  loader: () => import("../../features/kegs/InspectionPile/Issue"),
  loading: LoadingComponent,
});

const AsyncHelp = Loadable({
  loader: () => import("../../features/help"),
  loading: LoadingComponent,
});

const AsyncDispatch = Loadable({
  loader: () => import("../../features/stockControl/dispatch"),
  loading: LoadingComponent,
});

const AsyncReceive = Loadable({
  loader: () => import("../../features/stockControl/receive"),
  loading: LoadingComponent,
});

const AsyncReports = Loadable({
  loader: () => import("../../features/reports"),
  loading: LoadingComponent,
});

const AsyncCompanyDetailedPage = Loadable({
  loader: () =>
    import("../../features/companies/CompanyDetailedPage/CompanyDetailedPage"),
  loading: LoadingComponent,
});

const AsyncSettings = Loadable({
  loader: () => import("../../features/settings"),
  loading: LoadingComponent,
});

const AsyncBatchCompletePage = Loadable({
  loader: () => import("../../features/kegs/BatchComplete/BatchComplete"),
  loading: LoadingComponent,
});

const AsyncRecentActivityLog = Loadable({
  loader: () => import("../../features/activity"),
  loading: LoadingComponent,
});

const AsyncCompanyUsers = Loadable({
  loader: () => import("../../features/company-users/index"),
  loading: LoadingComponent,
});

const AsyncMostFilledKegs = Loadable({
  loader: () => import("../../features/kegs/MostFilledKegs/index.jsx"),
  loading: LoadingComponent,
});

const AsyncAllActionsPage = Loadable({
  loader: () => import("../../features/reports/allactions/index"),
  loading: LoadingComponent,
});

export default function Router() {
  const { claims, companyLink, isLoading } = useAuth();

  // Dev-only logging for debugging auth/claims state
  useEffect(() => {
    debugLog("[Router] Auth state changed:", {
      companyLink: companyLink || claims?.companyLink,
      hasClaims: !!claims,
      isAdmin: claims?.isAdmin,
      isLoading,
    });
  }, [
    claims,
    companyLink,
    isLoading,
  ]);

  // Show timeout-aware loading when claims.companyLink is not available
  const resolvedCompanyLink = companyLink || claims?.companyLink;
  const isWaitingForClaims = !resolvedCompanyLink;

  return (
    <DataProvider>
      {isWaitingForClaims ? (
        <LoadingWithTimeout
          context="Router waiting for claims.companyLink"
          loading={true}
          loadingText="Loading your session..."
          timeout={15000}
        />
      ) : (
        <div
          className="app-container"
          style={{
            display: "flex",
            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
            minHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <SideDrawer />

          <div
            className="page-content"
            style={{
              gridColumn: "span 11 / span 11",
              height: "100vh",
              overflowY: "auto",
              width: "100%",
            }}
          >
            <CheckVersion />

            <Switch>
              <Route
                component={Dashboard}
                path="/dashboard"
              />
              <Route
                component={MapPage}
                path="/map"
              />
              <Route
                component={AsyncKegFill}
                exact
                path="/keg-fill"
              />

              <Route
                component={AsyncWarehouseTransfer}
                exact
                path="/warehouse-transfer"
              />

              <Route
                component={AsyncKegList}
                exact
                path="/kegs-list"
              />

              <Route
                component={AsyncMostFilledKegs}
                exact
                path="/most-filled"
              />

              <Route
                component={AsyncAllActionsPage}
                exact
                path="/all-actions"
              />

              <Route
                component={AsyncKegList}
                exact
                path="/kegs-list/:status"
              />
              <Route
                component={AsyncKegDetailedPage}
                exact
                path="/kegs/:id"
              />

              <Route
                component={AsyncKegBatchUpdatePage}
                exact
                path="/keg-batch-update"
              />

              <Route
                component={AsyncInspectionPilePage}
                exact
                path="/inspection-pile"
              />

              <Route
                component={AsyncOnboardKegsPage}
                exact
                path="/onboard-kegs"
              />

              <Route
                component={AsyncFixKegPage}
                exact
                path="/fix/:id"
              />
              <Route
                component={AsyncIssueKegPage}
                exact
                path="/issue/:id"
              />

              <Route
                component={AsyncKegQuickViewPage}
                exact
                path="/keg-quick-view"
              />

              <Route
                component={AsyncDispatch}
                exact
                path="/dispatch"
              />
              <Route
                component={AsyncReceive}
                exact
                path="/receive"
              />
              <Route
                component={AsyncReports}
                path="/reports"
              />
              <Route
                component={AsyncHelp}
                exact
                path="/help"
              />
              <Route
                component={AsyncSettings}
                path="/settings"
              />
              <Route
                component={AsyncCompanyDetailedPage}
                path="/companies"
              />
              <Route
                component={AsyncCompanyUsers}
                path="/companies/users"
              />

              <Route
                component={AsyncBatchCompletePage}
                exact
                path="/batch-complete"
              />

              <Route
                component={AsyncRecentActivityLog}
                exact
                path="/activity-log/:collection/:id"
              />

              <Redirect to="/" />
            </Switch>
          </div>

          <Announcement />
        </div>
      )}
    </DataProvider>
  );
}
